/**
 * EtsyFreeShippingZonesSelector — pays sur lesquels le shipping Gelato
 * est absorbé dans le retail Etsy (free shipping baked).
 *
 * Refonte pricing 2026-05-16 (plan iridescent-sprouting-graham). Sur la
 * zone gratuite, l'atelier prend en charge le shipping → augmente le
 * retail Etsy en conséquence. Sur la zone lointaine, le buyer paie un
 * upcharge fees-compensé.
 *
 * Le PUT déclenche un backfill Gelato synchrone all-or-nothing : si
 * un nouveau pays est ajouté, on quote les SKUs actifs immédiatement
 * (latence 5-30s). Si Gelato plante ou timeout → setting inchangé,
 * toast erreur, 0 row partielle en DB.
 */

import { Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import Button from '@/components/ui/Button'
import Field from '@/components/ui/Field'
import Input from '@/components/ui/Input'
import {
  useEtsyFreeShippingZones,
  useUpdateEtsyFreeShippingZonesWithBackfill,
} from '@/hooks/useAtelierSettings'

import './SettingsOrderedList.css'

export default function EtsyFreeShippingZonesSelector() {
  const settingQuery = useEtsyFreeShippingZones()
  const updateMutation = useUpdateEtsyFreeShippingZonesWithBackfill()

  const serverValue = settingQuery.data?.value ?? null

  // Adjust-state-on-prop-change pattern (React 19 — pas de useEffect).
  const [zones, setZones] = useState<string[]>(serverValue ?? [])
  const [serverSnapshot, setServerSnapshot] = useState<string | null>(
    serverValue !== null ? JSON.stringify(serverValue) : null,
  )
  if (serverValue !== null) {
    const next = JSON.stringify(serverValue)
    if (next !== serverSnapshot) {
      setServerSnapshot(next)
      setZones(serverValue)
    }
  }

  const [newCountry, setNewCountry] = useState('')

  const removeAt = (idx: number) => {
    setZones(zones.filter((_, i) => i !== idx))
  }

  const addCountry = () => {
    const cleaned = newCountry.trim().toUpperCase()
    if (cleaned.length !== 2 || !/^[A-Z]{2}$/.test(cleaned)) {
      toast.error(`Code ISO-2 invalide : "${newCountry}"`)
      return
    }
    if (zones.includes(cleaned)) {
      toast.error(`"${cleaned}" est déjà dans la liste`)
      return
    }
    setZones([...zones, cleaned])
    setNewCountry('')
  }

  const handleSave = () => {
    updateMutation.mutate(
      { value: zones },
      {
        onSuccess: (data) => {
          if (data.backfilled_quotes > 0) {
            toast.success(
              `Zones mises à jour. Backfill Gelato : ${data.backfilled_skus} SKU` +
                `${data.backfilled_skus > 1 ? 's' : ''} synchronisé${
                  data.backfilled_skus > 1 ? 's' : ''
                }, ${data.backfilled_quotes} nouvelle${
                  data.backfilled_quotes > 1 ? 's' : ''
                } quote${data.backfilled_quotes > 1 ? 's' : ''} en cache.`,
            )
          } else {
            toast.success('Zones mises à jour (aucun backfill nécessaire).')
          }
        },
        onError: (err) => {
          toast.error(err.detail ?? 'Backfill Gelato a échoué. Aucune modification appliquée.')
        },
      },
    )
  }

  return (
    <div>
      <header className="settings-section-header">
        <div>
          <h2 className="ui-h4 settings-section-title">Pays free shipping (Etsy)</h2>
          <p className="ui-body ui-text-muted settings-section-subtitle">
            Pays sur lesquels tu absorbes les <strong>frais d&apos;expédition Gelato</strong> dans
            le retail Etsy (free shipping affiché à l&apos;acheteur). Sur les autres pays,
            l&apos;acheteur paie un upcharge automatiquement compensé pour les frais Etsy.
          </p>
          <p className="ui-body ui-text-muted settings-section-subtitle">
            <strong>Synchronisation Gelato automatique</strong> au save : pour chaque pays ajouté,
            on quote tes SKUs actifs (5-30s). Si Gelato plante, le setting reste inchangé — pas de
            silence.
          </p>
        </div>
      </header>

      <div className="atelier-shipping-fieldset">
        {zones.length === 0 ? (
          <p className="ui-body ui-text-muted">
            Liste vide → pas de free shipping baked. Tous les pays paient le shipping Gelato via
            upcharge.
          </p>
        ) : (
          <ol className="settings-ordered-list">
            {zones.map((country, idx) => (
              <li key={country} className="settings-ordered-list-row">
                <span className="ui-body settings-ordered-list-rank">{idx + 1}.</span>
                <span className="ui-body settings-ordered-list-name">{country}</span>
                <Button
                  variant="quiet"
                  size="sm"
                  type="button"
                  onClick={() => removeAt(idx)}
                  aria-label="Retirer"
                >
                  <Trash2 size={14} />
                </Button>
              </li>
            ))}
          </ol>
        )}

        <Field
          label="Ajouter un pays"
          helper="Code ISO-2 majuscules (ex: FR, US, GB). Max 50 pays."
        >
          {(controlProps) => (
            <div className="settings-ordered-list-add-row">
              <Input
                {...controlProps}
                value={newCountry}
                onChange={(e) => setNewCountry(e.target.value)}
                placeholder="ex: FR"
                maxLength={2}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addCountry()
                  }
                }}
              />
              <Button type="button" variant="neutral" onClick={addCountry}>
                <Plus size={14} />
                Ajouter
              </Button>
            </div>
          )}
        </Field>

        <div className="atelier-shipping-actions">
          <Button onClick={handleSave} disabled={updateMutation.isPending}>
            {updateMutation.isPending
              ? 'Synchronisation Gelato en cours… (peut prendre 30s)'
              : 'Enregistrer les zones'}
          </Button>
        </div>
      </div>
    </div>
  )
}
